import React, { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { motion } from "framer-motion";
import { PublicPackageCategoryOption } from "../../../api/contracts/package-ads";
import { LargeCard } from "./LargeCard";
import { categoryFitsGuests, UnitGuestCounts } from "../packageDetailLogic";

interface PackageCategoryChoiceProps {
  /** Bound categories, already ordered available-first by the caller. */
  categories: PublicPackageCategoryOption[];
  /** Guest counts for the unit being chosen for — decides which categories fit. */
  guests: UnitGuestCounts;
  chosenCategoryAdId: string | null;
  onChoose: (categoryAdId: string) => void;
  onClear: () => void;
  /** 1-based position of the unit being chosen for, and how many there are.
   *  Omitted for a single unit, where sequencing would be noise. */
  unitPosition?: { current: number; total: number };
  /** Drop the "Choose category" heading where the surrounding surface already
   *  carries it — the mobile search sheet labels its own section. */
  hideHeading?: boolean;
  /** How many other units already claimed each category, by category ad id.
   *  Held against the category's own `vacancy` so the last room cannot be
   *  chosen twice. Omitted where there is only one unit to choose for. */
  takenByOtherUnits?: Record<string, number>;
  /** Keep the cards one per row at every width. For a host whose column is
   *  narrower than the viewport suggests: the booking flow's is capped at
   *  634px by `max-w-container` minus the summary sidebar, so two-up there is
   *  311px a card whatever the screen — too narrow for the amenity chips and
   *  the action. The dialog and the search sheet, which do get the full width,
   *  leave this off. */
  singleColumn?: boolean;
}

/**
 * The package's category choice, rendered as full category cards.
 *
 * Deliberately reuses `LargeCard` — the same card the landing page shows for a
 * standalone category — so a room looks the same wherever a guest meets it, and
 * so the choice is made on a photo, size, description and amenities rather than
 * a name in a list.
 *
 * Ordering is the caller's (`sortCategoriesByAvailability`). A category that
 * cannot be taken stays on screen, disabled, with the reason why: it is part of
 * what the package offers, and hiding it would misrepresent the package.
 */
export const PackageCategoryChoice: React.FC<PackageCategoryChoiceProps> = ({
  categories,
  guests,
  chosenCategoryAdId,
  onChoose,
  onClear,
  unitPosition,
  hideHeading = false,
  takenByOtherUnits,
  singleColumn = false,
}) => {
  const { t } = useTranslation();

  /**
   * Bookable-now first. The caller orders by availability alone, which leaves a
   * category the guest cannot take — too few adults for it, say — sitting above
   * the one they can. Eligibility is per-unit, so only this component knows it.
   * Stable within each group, so the caller's availability order survives.
   */
  const entries = useMemo(() => {
    const decorated = categories.map((category, index) => {
      const isAvailable = category.available === true;
      const fitsGuests = categoryFitsGuests(category.capacity, guests);
      // `vacancy` is null until dates are picked, when nothing is claimable
      // anyway; treat "unknown" as unconstrained and let `available` decide.
      const taken = takenByOtherUnits?.[category.categoryAdId] ?? 0;
      const hasRoomLeft = category.vacancy == null || taken < category.vacancy;
      // A previewOnly binding reaches the client only in a PMS preview: the
      // category ad is not published, so it has real inventory (and can look
      // perfectly available) while being unsellable.
      const eligible =
        isAvailable && fitsGuests && hasRoomLeft && !category.previewOnly;
      // Explain an ineligible category even after another has been chosen — the
      // guest is comparing, and a bare greyed card teaches them nothing.
      const disabledReason = eligible
        ? null
        : category.previewOnly
          ? t("landing.package_detail.preview_only_category")
          : !isAvailable
            ? t("landing.package_detail.category_unavailable")
            : !hasRoomLeft
              ? t("landing.package_detail.category_all_taken")
              : guests.adult < category.capacity.adults.min
                ? t("landing.package_detail.category_requires_min_adults", {
                    min: category.capacity.adults.min,
                  })
                : t("landing.package_detail.category_guests_dont_fit");
      return { category, index, eligible, disabledReason };
    });
    return decorated.sort(
      (a, b) => Number(b.eligible) - Number(a.eligible) || a.index - b.index,
    );
  }, [categories, guests, takenByOtherUnits, t]);

  return (
    // Reopening a choice should read as the cards arriving, not as a jump cut.
    <motion.div
      initial={{ opacity: 0, y: 6 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: 6 }}
      transition={{ duration: 0.22, ease: "easeOut" }}
      className="flex flex-col gap-3"
      data-testid="package-category-choice"
    >
      <div
        className={`flex items-baseline gap-2 ${hideHeading ? "justify-end" : "justify-between"}`}
      >
        {!hideHeading && (
          <h3 className="text-sm font-semibold text-ink">
            {t("landing.package_detail.choose_category")}
          </h3>
        )}
        {unitPosition && unitPosition.total > 1 && (
          <span
            data-testid="package-category-choice-unit"
            className="shrink-0 text-xs text-ink-secondary"
          >
            {t("landing.package_detail.unit_progress", {
              current: unitPosition.current,
              total: unitPosition.total,
            })}
          </span>
        )}
      </div>

      {/* Two-up where the host gives the step its full width; one-up on mobile
          and wherever `singleColumn` says the column is too narrow for it. */}
      <div className={`grid grid-cols-1 gap-3 ${singleColumn ? "" : "lg:grid-cols-2"}`}>
        {entries.map(({ category, eligible, disabledReason }) => {
          const isSelected = chosenCategoryAdId === category.categoryAdId;

          return (
            <LargeCard
              key={category.categoryAdId}
              images={category.images}
              title={category.name}
              description={category.description}
              price={category.price ?? 0}
              area={
                category.sizeRange?.length
                  ? category.sizeRange.join(" - ")
                  : null
              }
              lengthRange={category.lengthRange?.join(" - ") ?? null}
              widthRange={category.widthRange?.join(" - ") ?? null}
              amenities={category.amenities}
              showPriceAndSelect
              isSelected={isSelected}
              disabled={!eligible && !isSelected}
              disabledReason={disabledReason}
              // Selecting the chosen card again clears it, so a guest can change
              // their mind without hunting for a separate control.
              onSelect={
                isSelected ? onClear : () => onChoose(category.categoryAdId)
              }
            />
          );
        })}
      </div>
    </motion.div>
  );
};
